import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { PollyClient, DescribeVoicesCommand, SynthesizeSpeechCommand, type VoiceId } from "@aws-sdk/client-polly";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-providers";
import NativeVideoPlayer from "../components/NativeVideoPlayer";
import { PREVIEW_LESSON } from "../data/previewContent";
import type { TtsVoice } from "../services/api";
import "../css/Courses.css";
import "../css/Welcome.css";

export const Route = createFileRoute("/welcome/step-1")({ 
  component: WelcomeStep1,
});

// Extract plain text from HTML (remove code blocks)
const extractPlainText = (html: string) => {
  if (typeof window === "undefined" || typeof DOMParser === "undefined") return html;
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const codeBlocks = doc.querySelectorAll("pre, code");
  codeBlocks.forEach((block) => block.remove());
  return doc.body.textContent?.replace(/\s+/g, " ").trim() || "";
};

// Split text into chunks for Polly (max 2800 chars)
const splitTextIntoChunks = (text: string, maxChars: number = 2800): string[] => {
  if (text.length <= maxChars) return [text];
  const chunks: string[] = [];
  const paragraphs = text.split(/\n\n+/);
  let currentChunk = '';
  
  for (const para of paragraphs) {
    if (!para.trim()) continue;
    if ((currentChunk + para).length <= maxChars) {
      currentChunk += (currentChunk ? '\n\n' : '') + para;
    } else {
      if (currentChunk) chunks.push(currentChunk);
      if (para.length > maxChars) {
        const sentences = para.match(/[^.!?]+[.!?]+/g) || [para];
        currentChunk = '';
        for (const sent of sentences) {
          if ((currentChunk + sent).length <= maxChars) {
            currentChunk += sent;
          } else {
            if (currentChunk) chunks.push(currentChunk);
            currentChunk = sent;
          }
        }
      } else {
        currentChunk = para;
      }
    }
  }
  if (currentChunk) chunks.push(currentChunk);
  return chunks.length > 0 ? chunks : [text.substring(0, maxChars)];
};

const buildAudioBlob = async (audioStream: unknown): Promise<Blob> => {
  if (audioStream instanceof Blob) return audioStream;
  if (audioStream instanceof ArrayBuffer) return new Blob([audioStream], { type: "audio/mpeg" });
  if (ArrayBuffer.isView(audioStream)) return new Blob([audioStream.buffer], { type: "audio/mpeg" });
  const isReadableStream = (stream: unknown): stream is ReadableStream<Uint8Array> => {
    if (typeof ReadableStream === "undefined") return false;
    return !!stream && typeof (stream as ReadableStream<Uint8Array>).getReader === "function";
  };
  if (isReadableStream(audioStream)) return new Response(audioStream).blob();
  throw new Error("Unexpected audio stream format");
};

function WelcomeStep1() {
  const navigate = useNavigate();
  
  // Polly setup
  const pollyRegion = import.meta.env.VITE_AWS_REGION || "us-east-1";
  const cognitoIdentityPoolId = import.meta.env.VITE_COGNITO_IDENTITY_POOL_ID;
  const pollyClient = useMemo(() => {
    if (!cognitoIdentityPoolId || typeof window === "undefined") return null;
    return new PollyClient({
      region: pollyRegion,
      credentials: fromCognitoIdentityPool({
        clientConfig: { region: pollyRegion },
        identityPoolId: cognitoIdentityPoolId,
      }),
    });
  }, [cognitoIdentityPoolId, pollyRegion]);
  
  // TTS state
  const [isNarrating, setIsNarrating] = useState(false);
  const [ttsVoices, setTtsVoices] = useState<TtsVoice[]>([]);
  const [voiceGender, setVoiceGender] = useState<'all' | 'female' | 'male'>('all');
  const [selectedVoiceId, setSelectedVoiceId] = useState('');
  const [isVoicesLoading, setIsVoicesLoading] = useState(false);
  const [isNarrationLoading, setIsNarrationLoading] = useState(false);
  const [currentChunkIndex, setCurrentChunkIndex] = useState(0);
  const [totalChunks, setTotalChunks] = useState(1);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUrlRef = useRef<string | null>(null);
  const ttsRequestRef = useRef(0);
  
  // Filter voices by gender
  const filteredVoices = useMemo(() => {
    if (voiceGender === 'all') return ttsVoices;
    return ttsVoices.filter((voice) => voice.gender?.toLowerCase() === voiceGender);
  }, [ttsVoices, voiceGender]);
  
  // The text to narrate (from the active section)
  const activeText = useMemo(() => {
    const html = `<p>In 1997, IBM's Deep Blue made history by defeating world chess champion Garry Kasparov in a six-game match. This wasn't just another chess game—it was a watershed moment that proved machines could master complex strategic thinking.</p>
    <p>Deep Blue could evaluate 200 million positions per second, demonstrating raw computational power combined with sophisticated algorithms. But more importantly, it showed that AI could compete with human expertise in domains requiring deep strategy and pattern recognition.</p>
    <p>This victory sparked a global conversation about artificial intelligence and its potential. What started as a chess match became a symbol of humanity's relationship with intelligent machines—a relationship we're still exploring today as AI becomes integral to everything from healthcare to creative work.</p>`;
    return extractPlainText(html);
  }, []);

  // Load voices from Polly
  useEffect(() => {
    if (!pollyClient) return;
    let isMounted = true;
    setIsVoicesLoading(true);

    const loadVoices = async () => {
      try {
        const response = await pollyClient.send(
          new DescribeVoicesCommand({
            Engine: "neural",
            LanguageCode: "en-US",
          })
        );
        const voices: TtsVoice[] = (response.Voices ?? [])
          .map((voice) => ({
            id: voice.Id ?? "",
            name: voice.Name ?? voice.Id ?? "Unknown voice",
            gender: voice.Gender ?? null,
            previewUrl: null,
          }))
          .filter((voice) => voice.id)
          .filter((voice) => !['Joanna', 'Gregory'].includes(voice.id));
        if (!isMounted) return;
        setTtsVoices(voices);
        if (voices.length > 0) setSelectedVoiceId(voices[0].id);
      } catch (err) {
        console.error("Failed to load voices:", err);
      } finally {
        if (!isMounted) return;
        setIsVoicesLoading(false);
      }
    };

    loadVoices();
    return () => {
      isMounted = false;
    };
  }, [pollyClient]);

  // Auto-select first voice when filter changes
  useEffect(() => {
    if (filteredVoices.length === 0) {
      setSelectedVoiceId('');
      return;
    }
    if (!filteredVoices.some((voice) => voice.id === selectedVoiceId)) {
      setSelectedVoiceId(filteredVoices[0].id);
    }
  }, [filteredVoices, selectedVoiceId]);

  const cleanupAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.onended = null;
      audioRef.current.onerror = null;
      audioRef.current.removeAttribute("src");
      audioRef.current.load();
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
  }, []);

  const stopNarration = useCallback(() => {
    ttsRequestRef.current += 1;
    cleanupAudio();
    setIsNarrating(false);
    setIsNarrationLoading(false);
  }, [cleanupAudio]);

  const playChunk = useCallback(async (text: string, requestId: number): Promise<void> => {
    if (!pollyClient) throw new Error("Polly client not available");
    if (ttsRequestRef.current !== requestId) throw new Error("Request cancelled");

    const response = await pollyClient.send(new SynthesizeSpeechCommand({
      Text: text,
      OutputFormat: "mp3",
      VoiceId: selectedVoiceId as VoiceId,
      Engine: "neural",
    }));
    const audioStream = response.AudioStream;
    if (!audioStream) throw new Error("Amazon Polly did not return audio");
    if (ttsRequestRef.current !== requestId) throw new Error("Request cancelled");

    const audioBlob = await buildAudioBlob(audioStream);
    if (ttsRequestRef.current !== requestId) throw new Error("Request cancelled");

    const audioUrl = URL.createObjectURL(audioBlob);

    return new Promise<void>((resolve, reject) => {
      const audio = new Audio();
      const cleanup = () => {
        audio.onended = null;
        audio.onerror = null;
        URL.revokeObjectURL(audioUrl);
      };
      audio.onended = () => {
        cleanup();
        resolve();
      };
      audio.onerror = () => {
        cleanup();
        reject(new Error("Failed to play audio chunk"));
      };
      audio.src = audioUrl;
      audio.play().catch((err) => {
        cleanup();
        reject(err);
      });
      audioRef.current = audio;
      audioUrlRef.current = audioUrl;
    });
  }, [pollyClient, selectedVoiceId]);

  const playNarration = useCallback(async () => {
    if (!activeText || !selectedVoiceId || !pollyClient) return;

    const requestId = ttsRequestRef.current + 1;
    ttsRequestRef.current = requestId;
    setIsNarrationLoading(true);
    cleanupAudio();

    try {
      const chunks = splitTextIntoChunks(activeText);
      setTotalChunks(chunks.length);
      setCurrentChunkIndex(0);
      setIsNarrating(true);
      setIsNarrationLoading(false);

      for (let i = 0; i < chunks.length; i++) {
        if (ttsRequestRef.current !== requestId) break;
        setCurrentChunkIndex(i);
        await playChunk(chunks[i], requestId);
        if (ttsRequestRef.current !== requestId) break;
      }

      if (ttsRequestRef.current === requestId) {
        cleanupAudio();
        setIsNarrating(false);
        setCurrentChunkIndex(0);
        setTotalChunks(1);
      }
    } catch (err) {
      console.error('Narration error:', err);
      if (ttsRequestRef.current === requestId) {
        cleanupAudio();
        setIsNarrating(false);
        setCurrentChunkIndex(0);
        setTotalChunks(1);
      }
    } finally {
      if (ttsRequestRef.current === requestId) {
        setIsNarrationLoading(false);
      }
    }
  }, [activeText, cleanupAudio, playChunk, pollyClient, selectedVoiceId]);

  const handleNarrationToggle = useCallback(() => {
    if (isNarrating || isNarrationLoading) {
      stopNarration();
    } else {
      playNarration();
    }
  }, [isNarrating, isNarrationLoading, playNarration, stopNarration]);

  const handleContinue = () => {
    stopNarration();
    navigate({ to: "/welcome/step-2" });
  };

  return (
    <div style={{ background: "#0a0f1a", minHeight: "100vh", color: "#f5f5f5", padding: "0" }}>
      {/* Sign In link for existing users */}
      <div className="welcome-signin-link">
        <Link to="/courses" className="signin-link">
          ...skip to Login instead
        </Link>
      </div>

      {/* Video Player */}
      <div style={{ maxWidth: "1400px", margin: "0 auto", padding: "40px 20px 20px" }}>
        <NativeVideoPlayer
          src="/videos/preview-lesson.mp4"
          title={PREVIEW_LESSON.title}
        />
      </div>

      {/* Content Section */}
      <div style={{ maxWidth: "900px", margin: "0 auto", padding: "20px" }}>
        {/* Narration Bar */}
        <div className="story-beat-header">
          <div className="story-beat-progress">
            <span>Section 2 of 3</span>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              value={voiceGender}
              onChange={(event) => setVoiceGender(event.target.value as 'all' | 'female' | 'male')}
              disabled={isVoicesLoading}
              style={{
                background: 'rgba(255, 255, 255, 0.08)',
                color: '#f5f5f5',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: 6,
                padding: '6px 10px',
                fontSize: '0.85rem'
              }}
            >
              <option value="all">All voices</option>
              <option value="female">Female voices</option>
              <option value="male">Male voices</option>
            </select>
            <select
              value={selectedVoiceId}
              onChange={(event) => setSelectedVoiceId(event.target.value)}
              disabled={isVoicesLoading || filteredVoices.length === 0}
              style={{
                background: 'rgba(255, 255, 255, 0.08)',
                color: '#f5f5f5',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: 6,
                padding: '6px 10px',
                fontSize: '0.85rem',
                minWidth: 160
              }}
            >
              {filteredVoices.length === 0 ? (
                <option value="">No voices available</option>
              ) : (
                filteredVoices.map((voice) => (
                  <option key={voice.id} value={voice.id}>
                    {voice.name}{voice.gender ? ` (${voice.gender})` : ''}
                  </option>
                ))
              )}
            </select>
            <button
              type="button"
              className="story-beat-toggle"
              onClick={handleNarrationToggle}
              disabled={!activeText || !selectedVoiceId || isVoicesLoading}
            >
              {isNarrationLoading ? "Loading..." : isNarrating ? "Stop narrator" : "Play narrator"}
            </button>
            {isNarrating && totalChunks > 1 && (
              <div style={{
                fontSize: '0.75rem',
                color: '#94a3b8',
                marginTop: 4,
                opacity: 0.8
              }}>
                Part {currentChunkIndex + 1} of {totalChunks}
              </div>
            )}
          </div>
        </div>

        {/* Section 1: Blurred Dummy */}
        <div className="story-beat story-beat--locked story-beat--muted">
          <h2>The Early History of AI</h2>
          <p>
            Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do
            eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut
            enim ad minim veniam, quis nostrud exercitation ullamco laboris.
          </p>
          <p>
            Excepteur sint occaecat cupidatat non proident, sunt in culpa qui
            officia deserunt mollit anim id est laborum.
          </p>
        </div>

        {/* Section 2: Active/Readable Section */}
        <div className="story-beat story-beat--open story-beat--active">
          <h2>The 1997 Deep Blue Victory</h2>
          <p>
            In 1997, IBM's Deep Blue made history by defeating world chess
            champion Garry Kasparov in a six-game match. This wasn't just
            another chess game—it was a watershed moment that proved machines
            could master complex strategic thinking.
          </p>
          <p>
            Deep Blue could evaluate 200 million positions per second,
            demonstrating raw computational power combined with sophisticated
            algorithms. But more importantly, it showed that AI could compete
            with human expertise in domains requiring deep strategy and
            pattern recognition.
          </p>
          <p>
            This victory sparked a global conversation about artificial
            intelligence and its potential. What started as a chess match
            became a symbol of humanity's relationship with intelligent
            machines—a relationship we're still exploring today as AI becomes
            integral to everything from healthcare to creative work.
          </p>

          {/* Continue Button */}
          <button
            className="story-beat-continue"
            onClick={handleContinue}
          >
            Try a Lab Challenge →
          </button>
        </div>

        {/* Section 3: Blurred Dummy */}
        <div className="story-beat story-beat--locked story-beat--muted">
          <h3>Modern AI Applications</h3>
          <p>
            Lorem ipsum dolor sit amet, consectetur adipiscing elit.
            Pellentesque habitant morbi tristique senectus et netus et
            malesuada fames ac turpis egestas.
          </p>
          <p>
            Aenean ultricies mi vitae est. Mauris placerat eleifend leo.
            Quisque sit amet est et sapien ullamcorper pharetra.
          </p>
        </div>
      </div>
    </div>
  );
}
